import { validateField, getFieldErrorClass } from "@/utils/fieldHighlighting";
import { useState, useEffect, useLayoutEffect, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Input } from '@/components/cms/ui/input';
import { Textarea } from '@/components/cms/ui/textarea';
import { Card } from '@/components/ui/card';
import { CardTitle, FieldLabel, PageTitle } from '@/components/ui/text';
import { Sheet, SheetContent, SheetTrigger } from '@/components/cms/ui/sheet';
import { UIImageUploadDialog } from '@/components/dialogs/ui-image-upload-dialog';
import { ExitConfirmationDialog } from '@/components/dialogs/exitConfirmationDialog';
import { DeleteDialog } from '@/components/dialogs/scene-project-delete-dialog';
import { LoadingSpinner } from '@/common/LoadingSpinner';
import { Project } from '@/types/cmsTypes';
import { ProjectInstance } from '@/types/projectTypes';
import { useProjectImages } from '@/modules/projectImages';
import { useAuthUser } from '@/hooks/useAuthUser';
import { useOrganization } from '@/hooks/useOrganization';
import { useServices } from '@/infrastructure/providers/ServiceProvider';
import { v1, toResult } from '@/infrastructure/api/v1/client';
import { projectPatchBody } from '@/infrastructure/api/v1/mappers/projects';
import { toast } from 'sonner';
import { ArrowLeftIcon, FloppyDiskIcon, ListIcon, GearIcon, TrashIcon, UploadSimpleIcon } from '@phosphor-icons/react';
import LureoButton from '@/common/LureoButton';

import projectPlaceholder from '@/assets/project-historical.jpg';
import { CardHeading } from '@/components/ui/card';
import { PageFrame } from '@/components/ui/page-frame';
import { useProjectEditData } from './projectEdit/useProjectEditData';
import { useUnsavedChangesGuard } from '@/hooks/useUnsavedChangesGuard';
import { ProjectHeaderImagesCard } from './projectEdit/ProjectHeaderImagesCard';
import { FORM_FIELDS, coordinatesIncomplete, initialFormFields, projectLocationJson, type ProjectFormFields } from './projectEdit/projectEditForm';

// Convert ProjectInstance to Project format for UI
const convertToProjectFormat = (projectInstance: ProjectInstance, heroImage: string): Project => {
  return {
    id: projectInstance.id,
    title: projectInstance.projectName || 'Untitled Project',
    description: projectInstance.description,
    heroImage: heroImage,
    duration: projectInstance.totalDuration ? Math.round(projectInstance.totalDuration / 60) : 0,
    tags: projectInstance.tags || [],
    status: projectInstance.isPublished ? 'live' : 'draft',
    locationInfo: projectInstance.locationInfo
      ? {
          latitude: projectInstance.locationInfo?.coordinates?.lat ?? 0,
          longitude: projectInstance.locationInfo?.coordinates?.lng ?? 0,
          address: projectInstance.locationInfo.address
        }
      : { latitude: 0, longitude: 0, address: '' },
    createdAt: projectInstance.createdAt ? new Date(projectInstance.createdAt) : new Date(),
    updatedAt: projectInstance.updatedAt ? new Date(projectInstance.updatedAt) : new Date(),
    storiesCount: projectInstance.totalScenes || 0,
    views: 0, // Not available in current schema
    completionRate: 0, // Not available in current schema
    authorId: projectInstance.userId
  };
};

export default function ProjectEdit() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuthUser();
  const { subscriptionPlan } = useOrganization();
  const services = useServices();


  // Validation mode - only show red rings after publish attempt
  const [showValidation, setShowValidation] = useState(false);

  // Check if we came from missing info dialog (has highlight param)
  if (!showValidation && new URLSearchParams(location.search).has('highlight')) {
    setShowValidation(true);
  }

  const isNewProject = location.state?.newProject;

  const { loadState, loadError, projectInstance, projectStories, reload } = useProjectEditData(id, user?.id);

  // Enable scrolling for ProjectEdit
  useLayoutEffect(() => {
    const originalOverflow = document.body.style.overflow || '';
    const originalScrollbarGutter = document.body.style.scrollbarGutter || '';
    
    document.body.style.overflow = 'auto';
    document.body.style.scrollbarGutter = 'stable';

    return () => {
      document.body.style.overflow = originalOverflow;
      document.body.style.scrollbarGutter = originalScrollbarGutter;
    };
  }, []);

  // Form state
  const [title, setTitle] = useState('');
  const [creatorName, setCreatorName] = useState('');
  const [description, setDescription] = useState('');
  const [duration, setDuration] = useState('');
  const [tags, setTags] = useState('');
  const [startLatitude, setStartLatitude] = useState('');
  const [startLongitude, setStartLongitude] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [isProjectSettingsCollapsed, setIsProjectSettingsCollapsed] = useState(false);
  const [isHeaderImagesCollapsed, setIsHeaderImagesCollapsed] = useState(false);
  
  // Check if any stories have address info to determine if location fields should be editable
  const hasStoriesWithAddress = useMemo(() => {
    return projectStories && projectStories.some(story => story.locationInfo?.address || 
      (story.locationInfo?.coordinates?.lat && story.locationInfo?.coordinates?.lng));
  }, [projectStories]);
  // Use the new project images hook
  const {
    projectImages,
    imageUploadDialog,
    handleImageUpload,
    handleImageSelect,
    handleCropApplied,
    handleImageDelete,
    handleLandscapeImageReorder,
    closeImageUploadDialog,
    readFailed: imagesReadFailed,
    fetchProjectImages
  } = useProjectImages(projectInstance?.id || '', user?.id || '');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<{ id: string; title: string; type: 'project' | 'scene' } | null>(null);



  // Create a stable reference to the image URL to prevent infinite loops
  const firstLandscapeImageUrl = projectImages.landscape[0]?.url || '';

  const project = useMemo(() => {
    if (!projectInstance) return null;
    // Use the first landscape image as the hero image, or fallback to default
    return convertToProjectFormat(projectInstance, firstLandscapeImageUrl || projectPlaceholder);
  }, [projectInstance, firstLandscapeImageUrl]);

  const formFields: ProjectFormFields = {
    title, creatorName, description, duration, tags, lat: startLatitude, lng: startLongitude, address, city,
  };

  // Compared with the seeded values, so null vs '' or a defaulted title never reads as unsaved
  const [baseline, setBaseline] = useState<ProjectFormFields | null>(null);

  // A fresh read reseeds each field the person has not edited; an edited field keeps what they typed
  const [formSource, setFormSource] = useState<{ projectInstance: typeof projectInstance; projectStories: typeof projectStories } | null>(null);
  if (!formSource || formSource.projectInstance !== projectInstance || formSource.projectStories !== projectStories) {
    setFormSource({ projectInstance, projectStories });
    if (projectInstance) {
      const init = initialFormFields(projectInstance, projectStories);
      const setters: Record<keyof ProjectFormFields, (value: string) => void> = {
        title: setTitle, creatorName: setCreatorName, description: setDescription, duration: setDuration, tags: setTags,
        lat: setStartLatitude, lng: setStartLongitude, address: setAddress, city: setCity,
      };
      for (const field of FORM_FIELDS) {
        const edited = baseline !== null && formFields[field] !== baseline[field];
        if (!edited) setters[field](init[field]);
      }
      setBaseline(init);
    }
  }

  useEffect(() => {
    // Clear location state after component mounts
    if (isNewProject) {
      window.history.replaceState({}, '');
    }
  }, [isNewProject]);

  const hasUnsavedChanges = () => baseline !== null && FORM_FIELDS.some((field) => formFields[field] !== baseline[field]);

  const [savingBeforeExit, setSavingBeforeExit] = useState(false);
  const guard = useUnsavedChangesGuard(hasUnsavedChanges());

  if (loadState === 'loading') {
    return (
      <PageFrame layout="centred">
        <LoadingSpinner size="xl" />
      </PageFrame>
    );
  }

  if (loadState !== 'ready' || !project) {
    const failed = loadState === 'error';
    return (
      <PageFrame layout="centred">
        <Card padding="lg" className="text-center">
          <PageTitle as="h2" className="mb-4">{failed ? "We couldn't load this project" : 'Project Not Found'}</PageTitle>
          <p className="mb-6 text-lureo-ink-subtle">
            {failed ? (loadError?.message || 'Something went wrong. Please try again.') : "The project you're looking for doesn't exist."}
          </p>
          <div className="flex justify-center gap-3">
            <LureoButton variant="no-outline" onClick={() => navigate('/dashboard')} className="h-9 w-9 p-0 md:w-auto md:px-4">
              <ArrowLeftIcon className="h-5 w-5" />
              <span className="hidden md:inline">Dashboard</span>
            </LureoButton>
            {failed && (
              <LureoButton variant="secondary" onClick={() => void reload()} className="h-9 rounded-lureo-radius-button-pill px-4">
                Try again
              </LureoButton>
            )}
          </div>
        </Card>
      </PageFrame>
    );
  }

  const isTemplateProject = projectInstance?.projectCategory === 'template';
  const canEditTemplateProjectName = services.subscriptionPlan.canEditTemplateProjectName(subscriptionPlan || '');
  const isNameReadOnly = isTemplateProject && !canEditTemplateProjectName;

  const saveProject = async (): Promise<boolean> => {
    if (!projectInstance || !user?.id) return false;

    try {
      // Parse tags from comma-separated string to array
      const tagsArray = tags
        .split(',')
        .map((tag) => tag.trim())
        .filter((tag) => tag.length > 0);

      if (!hasStoriesWithAddress && coordinatesIncomplete(formFields)) {
        toast.error('Enter both a latitude and a longitude, or leave both empty.');
        return false;
      }

      const updateData = {
        ...(!isNameReadOnly && { project_instance_display_name: title }),
        project_instance_source_name: creatorName,
        project_instance_description: description,
        // A location inherited from a story is shown, not saved onto the project
        ...(!hasStoriesWithAddress && { project_instance_location_info: projectLocationJson(formFields) }),
        project_instance_tags: tagsArray,
      };

      const response = await toResult(v1.PATCH('/api/v1/projects/{projectUniqueId}', {
        params: { path: { projectUniqueId: id } },
        body: projectPatchBody(updateData),
      }));

      if (response.error || !response.data) {
        throw response.error || new Error('No rows updated');
      }

      // Update project published status based on live scenes
      try {
        await services.project.updateProjectPublishedStatus(id);
      } catch (updateError) {
        console.error('Error updating project published status:', updateError);
        // Don't fail the save operation if project status update fails
      }
      return true;
    } catch (error) {
      console.error('Error saving project:', error);
      toast.error('Failed to save project');
      return false;
    }
  };

  const handleSave = async () => {
    if (!(await saveProject())) return;
    toast.success('Project saved successfully');
    guard.leave(() => navigate(`/project/${id}`));
  };

  // Publish saves first, then hands off to the project page's publish flow.
  const handlePublish = async () => {
    if (!(await saveProject())) return;
    guard.leave(() => navigate(`/project/${id}`, { state: { openPublish: true } }));
  };

  const handleDeleteProject = () => {
    if (!projectInstance) return;
    
    setItemToDelete({
      id: projectInstance.id,
      title: projectInstance.projectName || 'Untitled Project',
      type: 'project'
    });
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async (item: { id: string; title: string; type: 'project' | 'scene' }) => {
    if (!user?.id) return;

    if (item.type === 'project') {
      try {
        await services.project.deleteProject(id);

        toast.success('Project deleted successfully');
        guard.leave(() => navigate('/dashboard'));
      } catch (error) {
        console.error('Error deleting project:', error);
        toast.error('Failed to delete project');
      }
    }
  };





  // Select-all helpers for inputs/textareas
  const handleFocusSelectAll = (e: any) => {
    const target = e.target as HTMLInputElement | HTMLTextAreaElement;
    if (!target) return;
    try {
      // select() may throw for certain input types in some browsers
      if (target.select) target.select();
    } catch {
      // Selecting is a convenience; leave the field as it is
    }
    try {
      if (target.type !== 'number' && (target as any).setSelectionRange) {
        (target as any).setSelectionRange(0, target.value?.length ?? 0);
      }
    } catch {
      // setSelectionRange throws for input types without a text selection
    }
  };

  const handleMouseUpPreventDeselection = (e: any) => {
    // Prevent mouseup from clearing the selection after focus
    e.preventDefault();
  };


  const handleBackClick = () => navigate(`/project/${id}`);

  // The dialog stays open when the save failed
  const handleSaveAndExit = async () => {
    if (savingBeforeExit) return;
    setSavingBeforeExit(true);
    const saved = await saveProject();
    setSavingBeforeExit(false);
    if (!saved) return;
    toast.success('Project saved successfully');
    guard.proceed();
  };



  return (
    <PageFrame layout="content" className="cursor-default overflow-x-hidden">
      {/* Header */}
      <div className="sticky top-0 z-lureo-layer-bar surface-bar">
        <div className="container py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 md:gap-4">
              <LureoButton variant="no-outline" onClick={handleBackClick} className="h-9 w-9 p-0 md:w-auto md:px-4">
                <ArrowLeftIcon className="h-5 w-5" />
                <span className="hidden md:inline">Back to Project</span>
              </LureoButton>
              <div className="h-6 w-px bg-lureo-line" />
              <PageTitle>Edit Project</PageTitle>
            </div>

            {/* Desktop buttons */}
            <div className="hidden items-center gap-2 md:gap-3 lg:flex">
              <LureoButton className="h-9 rounded-lureo-radius-button-pill px-3" onClick={handleSave} variant="outline-2">
                <FloppyDiskIcon className="h-5 w-5" />
                Save Changes
              </LureoButton>
              <LureoButton className="h-9 rounded-lureo-radius-button-pill px-3" onClick={handlePublish} variant="secondary">
                <UploadSimpleIcon className="h-5 w-5" />
                Publish
              </LureoButton>
            </div>

            {/* Mobile/Tablet menu */}
            <div className="lg:hidden">
              <Sheet>
                <SheetTrigger asChild>
                  <LureoButton variant="outline-2" aria-label="Menu" className="h-9 w-9 rounded-full p-0">
                    <ListIcon className="h-5 w-5" />
                  </LureoButton>
                </SheetTrigger>
                <SheetContent side="right" className="w-80">
                  <div className="space-y-4 pt-6">
                    <LureoButton
                      onClick={() => {
                        handleSave();
                      }}
                      variant="outline-2"
                      className="h-9 w-full rounded-lureo-radius-button-pill">
                      <FloppyDiskIcon className="h-5 w-5" />
                      Save Changes
                    </LureoButton>
                    <LureoButton
                      onClick={() => {
                        handlePublish();
                      }}
                      variant="secondary"
                      className="h-9 w-full rounded-lureo-radius-button-pill">
                      <UploadSimpleIcon className="h-5 w-5" />
                      Publish
                    </LureoButton>
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </div>

      <div className="container py-6 md:py-8">
        {/* Mobile/Tablet Layout */}
        <div className="grid grid-cols-1 gap-6 md:gap-8 lg:hidden">
          <ProjectHeaderImagesCard
            projectImages={projectImages}
            readFailed={imagesReadFailed}
            onRetry={() => void fetchProjectImages()}
            handleImageUpload={handleImageUpload}
            handleImageDelete={handleImageDelete}
            handleLandscapeImageReorder={handleLandscapeImageReorder}
            showValidation={showValidation}
            open={!isHeaderImagesCollapsed}
            onOpenChange={(open) => setIsHeaderImagesCollapsed(!open)}
          />

          {/* Project Settings */}
          <Card elevation="high">
            <CardHeading icon={<GearIcon />} title="Settings" open={!isProjectSettingsCollapsed} onOpenChange={(open) => setIsProjectSettingsCollapsed(!open)} />

            {!isProjectSettingsCollapsed && (
              <div className="space-y-4">
                <div>
                  <FieldLabel variant="plain" mb htmlFor="project-edit-name-mobile">Name</FieldLabel>
                  <Input
                    id="project-edit-name-mobile"
                    value={title}
                    onChange={(e) => setTitle(e.target.value.slice(0, 20))}
                    onFocus={handleFocusSelectAll}
                    onMouseUp={handleMouseUpPreventDeselection}
                    placeholder="Enter project name..."
                    maxLength={20}
                    disabled={isNameReadOnly}
                    className={`select-text ${isNameReadOnly ? 'bg-lureo-neutral/50 cursor-not-allowed' : ''} ${getFieldErrorClass(!validateField('project_name', title), showValidation && !!projectInstance)}`}
                  />
                  <p className="text-lureo-12 text-lureo-ink-subtle mt-1 leading-4">
                    {isNameReadOnly ? 'Template project name cannot be changed' : `${title.length}/20 characters`}
                  </p>
                </div>

                <div>
                  <FieldLabel variant="plain" mb htmlFor="project-edit-creator-mobile">Creator Name</FieldLabel>
                  <Input
                    id="project-edit-creator-mobile"
                    value={creatorName}
                    onChange={(e) => setCreatorName(e.target.value)}
                    placeholder="Enter creator name..."
                    className={`select-text ${getFieldErrorClass(!validateField('project_source_name', creatorName), showValidation && !!projectInstance)}`}
                  />
                </div>

                <div>
                  <FieldLabel variant="plain" mb htmlFor="project-edit-description-mobile">Description</FieldLabel>
                  <Textarea
                    id="project-edit-description-mobile"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    onFocus={handleFocusSelectAll}
                    onMouseUp={handleMouseUpPreventDeselection}
                    placeholder="Describe your AR storytelling experience..."
                    rows={4}
                    className={`select-text ${getFieldErrorClass(!validateField('project_description', description), showValidation && !!projectInstance)}`}
                  />
                </div>

                <div>
                  <FieldLabel variant="plain" mb htmlFor="project-edit-duration-mobile">Total Duration (minutes)</FieldLabel>
                  <Input
                    id="project-edit-duration-mobile"
                    type="number"
                    value={duration}
                    disabled={true}
                    placeholder="30"
                    min="1"
                    className="bg-lureo-neutral/50 cursor-not-allowed select-text"
                  />
                  <p className="text-lureo-12 text-lureo-ink-subtle mt-1 leading-4">
                    Duration is automatically calculated from story content
                  </p>
                </div>

                  <FieldLabel variant="plain" mb htmlFor="project-edit-tags-mobile">Tags (comma separated)</FieldLabel>
                  <Input
                    id="project-edit-tags-mobile"
                    value={tags}
                    onChange={(e) => setTags(e.target.value)}
                    placeholder="History, Architecture, Medieval..."
                    onFocus={handleFocusSelectAll}
                    onMouseUp={handleMouseUpPreventDeselection}
                    className={`select-text ${getFieldErrorClass(!validateField('project_tags', tags), showValidation && !!projectInstance)}`}
                  />

                

                <div>
                  <FieldLabel variant="plain" mb>Starting Point Coordinates</FieldLabel>
                  <div className="grid grid-cols-2 gap-2">
                    <Input
                      type="number"
                      value={startLatitude}
                      onChange={(e) => setStartLatitude(e.target.value)}
                      placeholder="51.5074"
                      step="0.0001"
                      onFocus={handleFocusSelectAll}
                      onMouseUp={handleMouseUpPreventDeselection}
                      disabled={hasStoriesWithAddress}
                      className={(hasStoriesWithAddress ? "bg-lureo-neutral/50 cursor-not-allowed " : "") + "select-text"}
                    />
                    <Input
                      type="number"
                      value={startLongitude}
                      onChange={(e) => setStartLongitude(e.target.value)}
                      placeholder="-0.1278"
                      step="0.0001"
                      onFocus={handleFocusSelectAll}
                      onMouseUp={handleMouseUpPreventDeselection}
                      disabled={hasStoriesWithAddress}
                      className={(hasStoriesWithAddress ? "bg-lureo-neutral/50 cursor-not-allowed " : "") + "select-text"}
                    />
                  </div>
                  <p className="mt-1 text-lureo-12 text-lureo-ink-subtle leading-4">Latitude, Longitude</p>
                  {hasStoriesWithAddress && (
                    <p className="text-lureo-12 text-lureo-ink-subtle mt-1 leading-4">
                      Coordinates are inherited from story locations
                    </p>
                  )}
                </div>

                <div>
                  <FieldLabel variant="plain" mb htmlFor="project-edit-address-mobile">Starting Point Address</FieldLabel>
                  <Input
                    id="project-edit-address-mobile"
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    placeholder="e.g., 123 Main Street, City, Country"
                    autoComplete="off"
                    onFocus={handleFocusSelectAll}
                    onMouseUp={handleMouseUpPreventDeselection}
                    disabled={hasStoriesWithAddress}
                    className={(hasStoriesWithAddress ? "bg-lureo-neutral/50 cursor-not-allowed " : "") + "select-text"}
                  />
                  <p className="mt-1 text-lureo-12 text-lureo-ink-subtle leading-4">Human-readable address for better context</p>
                  {hasStoriesWithAddress && (
                    <p className="text-lureo-12 text-lureo-ink-subtle mt-1 leading-4">
                      Address is inherited from story locations
                    </p>
                  )}
                </div>

              </div>
            )}
          </Card>

          {/* Danger Zone */}
          <Card elevation="high">
            <CardTitle as="h3" className="mb-4 flex items-center">
              <TrashIcon className="mr-2 h-5 w-5 text-lureo-danger-ink" />
              Danger Zone
            </CardTitle>
            <p className="mb-4 text-lureo-14 text-lureo-ink-subtle leading-5">
              Permanently delete this project and all its stories. This action cannot be undone.
            </p>
            <LureoButton
              variant="danger"
              className="w-full h-9 rounded-lureo-radius-button-pill"
              onClick={handleDeleteProject}>
              Delete Project
            </LureoButton>
          </Card>
        </div>

        {/* Desktop Layout */}
        <div className="hidden lg:block">
          <div className="mb-6 space-y-6">
            <ProjectHeaderImagesCard
              projectImages={projectImages}
              readFailed={imagesReadFailed}
              onRetry={() => void fetchProjectImages()}
              handleImageUpload={handleImageUpload}
              handleImageDelete={handleImageDelete}
              handleLandscapeImageReorder={handleLandscapeImageReorder}
              showValidation={showValidation}
              open={!isHeaderImagesCollapsed}
              onOpenChange={(open) => setIsHeaderImagesCollapsed(!open)}
            />

            {/* Project Settings */}
            <Card elevation="high">
              <CardHeading icon={<GearIcon />} title="Settings" open={!isProjectSettingsCollapsed} onOpenChange={(open) => setIsProjectSettingsCollapsed(!open)} />

              {!isProjectSettingsCollapsed && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <FieldLabel variant="plain" mb htmlFor="project-edit-name">Name</FieldLabel>
                    <Input
                      id="project-edit-name"
                      value={title}
                      onChange={(e) => setTitle(e.target.value.slice(0, 20))}
                      onFocus={handleFocusSelectAll}
                      onMouseUp={handleMouseUpPreventDeselection}
                      placeholder="Enter project name..."
                      maxLength={20}
                      autoComplete="off"
                      disabled={isNameReadOnly}
                      className={`select-text ${isNameReadOnly ? 'bg-lureo-neutral/50 cursor-not-allowed' : ''} ${getFieldErrorClass(!validateField('project_name', title), showValidation && !!projectInstance)}`}
                    />
                    <p className="text-lureo-12 text-lureo-ink-subtle mt-1 leading-4">
                      {isNameReadOnly ? 'Template project names cannot be changed' : `${title.length}/20 characters`}
                    </p>
                  </div>

                  <div>
                    <FieldLabel variant="plain" mb htmlFor="project-edit-creator">Creator Name</FieldLabel>
                    <Input
                      id="project-edit-creator"
                      value={creatorName}
                      onChange={(e) => setCreatorName(e.target.value)}
                      placeholder="Creator, Studio, Company, etc."
                      className={`select-text ${getFieldErrorClass(!validateField('project_source_name', creatorName), showValidation && !!projectInstance)}`}
                    />
                  </div>

                  <div className="col-span-2">
                    <FieldLabel variant="plain" mb htmlFor="project-edit-description">Description</FieldLabel>
                    <Textarea
                      id="project-edit-description"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      onFocus={handleFocusSelectAll}
                      onMouseUp={handleMouseUpPreventDeselection}
                      placeholder="Describe your AR storytelling experience..."
                      rows={3}
                      className={`select-text ${getFieldErrorClass(!validateField('project_description', description), showValidation && !!projectInstance)}`}
                    />
                  </div>

                    <div className="col-span-2 grid grid-cols-2 gap-2">
                      <div>
                      <FieldLabel variant="plain" mb htmlFor="project-edit-tags">Tags (comma separated)</FieldLabel>
                      <Input
                        id="project-edit-tags"
                        value={tags}
                        onChange={(e) => setTags(e.target.value)}
                        placeholder="History, Architecture, Medieval..."
                        autoComplete="off"
                        onFocus={handleFocusSelectAll}
                        onMouseUp={handleMouseUpPreventDeselection}
                        className={`select-text ${getFieldErrorClass(!validateField('project_tags', tags), showValidation && !!projectInstance)}`}
                      />
                      </div>
                      <div>
                      <FieldLabel variant="plain" mb htmlFor="project-edit-duration">Total Duration (minutes)</FieldLabel>
                      <Input
                        id="project-edit-duration"
                        type="number"
                        value={duration}
                        disabled={true}
                        placeholder="30"
                        min="1"
                        className="bg-lureo-neutral/50 cursor-not-allowed select-text"
                      />
                      <p className="text-lureo-12 text-lureo-ink-subtle mt-1 leading-4">
                        Duration is automatically calculated from story content
                      </p>
                      </div>
                    </div>


                  <div className="col-span-2">
                    <FieldLabel variant="plain" mb>Starting Point Coordinates</FieldLabel>
                    <div className="grid grid-cols-2 gap-2">
                      <Input
                        type="number"
                        value={startLatitude}
                        onChange={(e) => setStartLatitude(e.target.value)}
                        placeholder="51.5074"
                        step="0.0001"
                        autoComplete="off"
                        onFocus={handleFocusSelectAll}
                        onMouseUp={handleMouseUpPreventDeselection}
                        disabled={hasStoriesWithAddress}
                        className={(hasStoriesWithAddress ? "bg-lureo-neutral/50 cursor-not-allowed " : "") + "select-text"}
                      />
                      <Input
                        type="number"
                        value={startLongitude}
                        onChange={(e) => setStartLongitude(e.target.value)}
                        placeholder="-0.1278"
                        step="0.0001"
                        autoComplete="off"
                        onFocus={handleFocusSelectAll}
                        onMouseUp={handleMouseUpPreventDeselection}
                        disabled={hasStoriesWithAddress}
                        className={(hasStoriesWithAddress ? "bg-lureo-neutral/50 cursor-not-allowed " : "") + "select-text"}
                      />
                    </div>
                    <p className="mt-1 text-lureo-12 text-lureo-ink-subtle leading-4">Latitude, Longitude</p>
                    {hasStoriesWithAddress && (
                      <p className="text-lureo-12 text-lureo-ink-subtle mt-1 leading-4">
                        Coordinates are inherited from story locations
                      </p>
                    )}
                  </div>

                  <div className="col-span-2">
                    <FieldLabel variant="plain" mb htmlFor="project-edit-address">Starting Point Address</FieldLabel>
                    <Input
                      id="project-edit-address"
                      value={address}
                      onChange={(e) => setAddress(e.target.value)}
                      placeholder="e.g., 123 Main Street, City, Country"
                      autoComplete="off"
                      onFocus={handleFocusSelectAll}
                      onMouseUp={handleMouseUpPreventDeselection}
                      disabled={hasStoriesWithAddress}
                      className={(hasStoriesWithAddress ? "bg-lureo-neutral/50 cursor-not-allowed " : "") + "select-text"}
                    />
                    <p className="mt-1 text-lureo-12 text-lureo-ink-subtle leading-4">Human-readable address for better context</p>
                    {hasStoriesWithAddress && (
                      <p className="text-lureo-12 text-lureo-ink-subtle mt-1 leading-4">
                        Address is inherited from story locations
                      </p>
                    )}
                  </div>
                </div>
              )}
            </Card>
          </div>

          {/* Danger Zone */}
          <div className="mt-8">
            <Card elevation="high">
              <CardTitle as="h3" className="mb-4 flex items-center">
                <TrashIcon className="mr-2 h-5 w-5 text-lureo-danger-ink" />
                Danger Zone
              </CardTitle>
              <p className="mb-4 text-lureo-14 text-lureo-ink-subtle leading-5">
                Permanently delete this project and all its stories. This action cannot be undone.
              </p>
              <LureoButton
                variant="danger"
                className="w-full h-9 rounded-lureo-radius-button-pill"
                onClick={handleDeleteProject}>
                Delete Project
              </LureoButton>
            </Card>
          </div>
        </div>

        {/* Image Upload Dialog */}
        <UIImageUploadDialog
          open={imageUploadDialog.open}
          onOpenChange={() => closeImageUploadDialog()}
          onImageSelect={handleImageSelect}
          onCropApplied={(data) => handleCropApplied(data)}
          title={imageUploadDialog.title}
          description={imageUploadDialog.imageCategory === 'ProjectPortrait' 
            ? "Upload a portrait image for your project or generate one using AI."
            : "Upload a landscape image for your project or generate one using AI."
          }
          currentImage={imageUploadDialog.imageCategory === 'ProjectPortrait' 
            ? projectImages.portrait?.url || null
            : imageUploadDialog.imageId 
              ? projectImages.landscape.find(img => img.id === imageUploadDialog.imageId)?.url || null
              : null
          }
          currentOriginalImage={imageUploadDialog.imageCategory === 'ProjectPortrait' 
            ? projectImages.portrait?.originalUrl || null
            : imageUploadDialog.imageId 
              ? projectImages.landscape.find(img => img.id === imageUploadDialog.imageId)?.originalUrl || null
              : null
          }
          imageCategory={imageUploadDialog.imageCategory || 'ProjectLandscape'}
          projectInstanceId={projectInstance?.id}
          enableCrop={true}
          closeOnRemove={true}
        />

        {/* Exit Confirmation Dialog */}
        <ExitConfirmationDialog
          open={guard.asking}
          onOpenChange={(open) => {
            if (!open && !savingBeforeExit) guard.cancel();
          }}
          onSaveAndExit={handleSaveAndExit}
          onDiscardAndExit={guard.proceed}
          saving={savingBeforeExit}
        />

        {/* Delete Dialog */}
        <DeleteDialog
          open={deleteDialogOpen}
          onOpenChange={setDeleteDialogOpen}
          onConfirm={handleConfirmDelete}
          onCancel={() => setDeleteDialogOpen(false)}
          item={itemToDelete}
        />
      </div>
    </PageFrame>
  );
}

